/**
 * notionSnapshot — lift a Notion database read into a SemanticSnapshot.
 *
 * A thin adapter over the shared reference-dialect builder: Notion pages are
 * reference objects, relation properties are typed cross-references (the
 * property name is the cue), the page tree is the parent chain, and a
 * select/status option color is the category color. All the real work — and
 * the uncertainty policy — lives in referenceDialectSnapshot, shared with
 * Linear.
 */

import type { NotionDatabaseRead } from './notionSemanticApi';
import type { MiroColorRule, RelationshipCueRule, TypeMapRule } from '../fieldMappingRegistry';
import { NOTION_DEFAULT_RELATIONSHIP_MAP, NOTION_UNNAMED_RELATION } from './profiles/notion';
import {
  buildReferenceSnapshot,
  type ReferenceDialectRead,
} from './referenceDialectSnapshot';
import type { SemanticSnapshot } from './snapshot';

export interface NotionSnapshotMaps {
  relationshipMap?: RelationshipCueRule[];
  typeMap?: TypeMapRule[];
  colorRules?: MiroColorRule[];
}

export function buildNotionSnapshot(
  read: NotionDatabaseRead,
  maps: NotionSnapshotMaps,
  options: { capturedAt?: string } = {},
): SemanticSnapshot {
  const dialectRead: ReferenceDialectRead = {
    connectorType: 'notion',
    container: { id: read.database.id, title: read.database.title, url: read.database.url },
    objects: read.pages.map(page => ({
      externalId: page.externalId,
      ...(page.externalUrl ? { externalUrl: page.externalUrl } : {}),
      title: page.title,
      ...(page.description ? { description: page.description } : {}),
      ...(page.categoryColor ? { categoryColor: page.categoryColor } : {}),
      ...(page.parentPageId ? { parentExternalId: page.parentPageId } : {}),
      relations: page.relations.map(r => ({ cue: r.property, targetExternalIds: r.targetExternalIds })),
    })),
  };

  return buildReferenceSnapshot(
    dialectRead,
    maps,
    {
      relationshipMap: NOTION_DEFAULT_RELATIONSHIP_MAP,
      containmentCueValue: 'page',
      unnamedRelation: NOTION_UNNAMED_RELATION,
    },
    options,
  );
}
